import { useEffect, useState } from "react";
import Typography from "@mui/material/Typography";
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  IconButton,
  Pagination,
  Stack,
  TextField,
  Skeleton,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  BarChart,
  Bar,
  XAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  YAxis,
} from "recharts";
import dayjs from "dayjs";
import { MdOutlineClear } from "react-icons/md";
import { Reports } from "../../../app/slices/reportsSlice";

const TeamLeaderOrdersPerDaysChart = () => {
  const dispatch = useDispatch();

  const { data = [] } = useSelector(
    (state) => state.reports.teamLeaderOrdersPerDays,
  );
  const loading = useSelector((state) => state.reports.teamLeaderOrdersPerDays.loading);

  const [leadersData, setLeadersData] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    dispatch(Reports.getTeamLeaderOrdersPerDays());
  }, [dispatch]);

  useEffect(() => {
    if (!Array.isArray(data)) {
      setLeadersData([]);
      return;
    }

    setLeadersData(
      data.filter((leader) => {
        const name = leader?.leader_name || "";
        return name.toLowerCase().startsWith(search.toLowerCase());
      }),
    );
  }, [data, search]);

  const itemsPerPage = 20;
  const [currentPage, setCurrentPage] = useState(1);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = leadersData.slice(indexOfFirstItem, indexOfLastItem);

  const totalItems = leadersData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const getDisplayName = (leader) => leader?.leader_name || "-";

  const getIdentifier = (leader) => leader?.leader_id || null;

  return (
    <Card>
      <CardHeader
        title={
          <Typography variant="h4" color={"purple"}>
            احصائيات مبيعات قادة الفرق
          </Typography>
        }
        subheader={
          <Stack direction="column" spacing={1}>
            <Typography variant="caption">
              {`${dayjs()
                .subtract(1, "week")
                .format("YYYY-MM-DD")} - ${dayjs().format("YYYY-MM-DD")}`}
            </Typography>
            <Stack alignItems="start" direction={"row"} spacing={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <TextField
                    size="small"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setCurrentPage(1);
                    }}
                    label="بحث عن الاسم"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={1}>
                  <IconButton
                    color="primary"
                    onClick={() => {
                      setSearch("");
                      setCurrentPage(1);
                    }}
                  >
                    <MdOutlineClear />
                  </IconButton>
                </Grid>
              </Grid>
            </Stack>
          </Stack>
        }
      ></CardHeader>
      <CardContent>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          {loading
            ? Array.from(new Array(4)).map((_, index) => (
                <Grid item xs={6} key={index}>
                  <Card>
                    <Stack
                      spacing={1}
                      justifyContent="center"
                      alignItems="center"
                      sx={{ py: 2 }}
                    >
                      <Skeleton variant="text" width={200} height={60} />
                      <Skeleton variant="text" width={250} height={30} />
                    </Stack>
                    <Divider sx={{ m: 2 }} />
                    <Skeleton variant="rectangular" width="100%" height={400} />
                  </Card>
                </Grid>
              ))
            : currentItems.length > 0
              ? currentItems.map((leader, index) => {
                  const chartData = Array.isArray(leader?.result)
                    ? leader.result.map((item) => ({
                        ...item,
                        total_order_amount:
                          parseInt(item.total_order_amount, 10) || 0,
                      }))
                    : [];

                  return (
                    <Grid item xs={6} key={`${getIdentifier(leader) ?? index}`}>
                      <Card>
                        <Stack
                          spacing={1}
                          justifyContent="center"
                          alignItems="center"
                        >
                          <Typography variant="h3">
                            {getDisplayName(leader)}
                          </Typography>
                          <Typography fontSize={18}>
                            مجموع ديون النقاط :{" "}
                            {Number(leader?.debt).toLocaleString()} د.ع
                          </Typography>
                        </Stack>
                        <Divider sx={{ m: 2 }} />
                        <ResponsiveContainer width="100%" height={400}>
                          <BarChart
                            style={{ direction: "ltr" }}
                            width={500}
                            height={300}
                            data={chartData}
                            margin={{
                              top: 5,
                              right: 30,
                              left: 20,
                              bottom: 5,
                            }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="order_date" />
                            <YAxis
                              tickFormatter={(value) =>
                                Number(value).toLocaleString()
                              }
                            />
                            <Tooltip />
                            <Legend />
                            <Bar
                              type="monotone"
                              dataKey="total_order_amount"
                              name="المبيعات"
                              fill="#8884d8"
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </Card>
                    </Grid>
                  );
                })
              : null}
          <Grid item xs={12}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default TeamLeaderOrdersPerDaysChart;
